// ---------------------------------------------------------------------------
// The paywall UI: plan cards (PlanPicker) and the global out-of-credits dialog
// (UpgradePrompt) that opens automatically when the backend answers 402. Both
// hand off to Stripe Checkout — no card data ever touches the app.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckIcon from '@mui/icons-material/Check';
import { startCheckout } from '../billing/api';
import { useCredits } from '../billing/credits';
import { useAppStore } from '../store/useAppStore';

interface PlanCard {
  id: 'free' | 'plus' | 'pro';
  name: string;
  price: string;
  credits: string;
  blurb: string;
  highlight?: boolean;
}

const PLANS: PlanCard[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    credits: '25 AI credits / month',
    blurb: 'Try the coach: profile setup, a handful of graded answers, and question browsing.',
  },
  {
    id: 'plus',
    name: 'Plus',
    price: '$19.99/mo',
    credits: '400 AI credits / month',
    blurb: 'Serious prep: regular graded practice, answer generation, and school research.',
    highlight: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$39.99/mo',
    credits: '1,500 AI credits / month',
    blurb: 'All-in interview season: daily mock interviews, dossiers, and unlimited-feeling practice.',
  },
];

/** Plan cards with upgrade buttons. Used in Settings and the out-of-credits dialog. */
export function PlanPicker({ currentTier, compact = false }: { currentTier: string; compact?: boolean }) {
  const showToast = useAppStore((s) => s.showToast);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);

  const upgrade = (plan: 'plus' | 'pro') => {
    setBusyPlan(plan);
    startCheckout(plan).catch((e: Error) => {
      showToast(e.message, 'error');
      setBusyPlan(null);
    });
  };

  const visible = PLANS.filter((p) => (compact ? p.id === 'pro' : true));

  return (
    <Grid container spacing={1.5}>
      {visible.map((plan) => {
        const isCurrent = plan.id === currentTier;
        const upgradable = plan.id !== 'free' && !isCurrent;
        return (
          <Grid item xs={12} sm={compact ? 12 : 4} key={plan.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderColor: plan.highlight ? 'primary.main' : undefined,
                borderWidth: plan.highlight ? 2 : 1,
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={0.75} sx={{ height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {plan.name}
                    </Typography>
                    {plan.highlight && <Chip size="small" color="primary" label="Popular" />}
                    {isCurrent && <Chip size="small" label="Current plan" />}
                  </Stack>
                  <Typography variant="h6">{plan.price}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CheckIcon fontSize="inherit" color="success" />
                    <Typography variant="body2">{plan.credits}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {plan.blurb}
                  </Typography>
                  {upgradable && (
                    <Button
                      variant={plan.highlight ? 'contained' : 'outlined'}
                      size="small"
                      disabled={busyPlan !== null}
                      onClick={() => upgrade(plan.id as 'plus' | 'pro')}
                    >
                      {busyPlan === plan.id ? 'Opening checkout…' : `Upgrade to ${plan.name}`}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

/**
 * Global out-of-credits paywall. Mounted once in the app shell; opens each
 * time the backend actually refuses an AI call with 402 (credits.exhaustedEvents),
 * so a dismissed dialog comes back on the next refused attempt — but a mere
 * status fetch that notices the allowance is used up never pops it.
 */
export default function UpgradePrompt() {
  const credits = useCredits();
  const [acknowledged, setAcknowledged] = useState(0);

  const open = credits.exhaustedEvents > acknowledged;
  const dismiss = () => setAcknowledged(credits.exhaustedEvents);
  const tier = credits.tier ?? 'free';

  return (
    <Dialog open={open} onClose={dismiss} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <RocketLaunchIcon color="primary" />
          <Box>You&apos;ve used your AI credits for this month</Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {credits.used !== null && credits.allowance !== null
            ? `You've used ${credits.used} of ${credits.allowance} included AI credits. `
            : ''}
          {tier === 'pro'
            ? 'Your credits reset at the start of next month. Everything you’ve already built — questions, answers, feedback — stays fully available meanwhile.'
            : 'Upgrade to keep practicing with graded answers, school research, and mock interviews — or wait until your credits reset next month.'}
        </Typography>
        {tier !== 'pro' && <PlanPicker currentTier={tier} />}
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={dismiss}>Not now</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
